import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, HelpCircle, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { CustomNodeData, IntegrationsNodeData, ProcessingNodeData } from '../types/nodes';
import { HelpMenu } from './HelpMenu';

import { ValidationResultDisplay } from './shared/ValidationResultDisplay';

export interface NodeDrawerProps {
	isOpen: boolean;
	onClose: () => void;
	nodeData: CustomNodeData;
	onSave: (data: CustomNodeData) => void;
	onChange?: (data: CustomNodeData) => void; // Add onChange callback for wizard nodes
	children?: React.ReactNode;
	// Help menu callback
	onHelpMenuOpen?: () => void;
	// Wizard navigation props
	wizardMode?: boolean;
	currentStep?: number;
	totalSteps?: number;
	canProceedToNext?: boolean;
	onPreviousStep?: () => void;
	onNextStep?: () => void;
	onCompleteWizard?: () => Promise<void>;
	// Validation state for two-step complete setup
	validationAttempted?: boolean;
	validationFailed?: boolean;
	// Loading state for complete setup button
	isCompletingSetup?: boolean;
}

export function NodeDrawer({
	isOpen,
	onClose,
	nodeData,
	onSave,
	onChange,
	children,
	onHelpMenuOpen,
	wizardMode = false,
	currentStep = 1,
	totalSteps = 4,
	canProceedToNext = true,
	onPreviousStep,
	onNextStep,
	onCompleteWizard,
	validationAttempted = false,
	validationFailed = false,
	isCompletingSetup = false,
}: NodeDrawerProps) {
	const [localData, setLocalData] = useState<CustomNodeData>(nodeData);
	const [isHelpMenuOpen, setIsHelpMenuOpen] = useState(false);
	const [isValidating, setIsValidating] = useState(false);
	const [validationResult, setValidationResult] = useState<any>(null);
	const [showValidationErrors, setShowValidationErrors] = useState(false);
	const [simpleModeValidationAttempted, setSimpleModeValidationAttempted] = useState(false);
	const [simpleModeValidationFailed, setSimpleModeValidationFailed] = useState(false);
	const labelSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const validationFunctionRef = useRef<(() => Promise<boolean>) | null>(null);

	// Reset local data when drawer opens with new node data
	useEffect(() => {
		if (isOpen) {
			setLocalData(nodeData);
		}
	}, [isOpen, nodeData]);

	const handleSave = async () => {
		// Debug logging to understand the issue
		console.log('🔍 NodeDrawer.handleSave - Debug Info:', {
			wizardMode,
			onCompleteWizard: !!onCompleteWizard,
			validationFunctionRef: !!validationFunctionRef.current,
			simpleModeValidationAttempted,
			simpleModeValidationFailed,
			isValidating,
		});

		// If we're in wizard mode and have an onCompleteWizard function, use that instead
		// This ensures the wizard validation pattern is followed
		if (wizardMode && onCompleteWizard) {
			console.log('🔍 NodeDrawer.handleSave - Using wizard mode with onCompleteWizard');
			try {
				await onCompleteWizard();
				// The wizard validation logic will handle saving and closing
				console.log('🔍 NodeDrawer.handleSave - Wizard completion successful');
				return;
			} catch (error) {
				console.error('Wizard completion failed:', error);
				return;
			}
		}

		console.log('🔍 NodeDrawer.handleSave - Using fallback validation logic');
		// Fallback to the old validation logic for non-wizard nodes
		// If there's a validation function, run it first
		if (validationFunctionRef.current) {
			setIsValidating(true);
			setShowValidationErrors(true);
			try {
				const isValid = await validationFunctionRef.current();
				setSimpleModeValidationAttempted(true);

				if (!isValid) {
					// Don't save if validation failed, but show the errors
					// This implements the two-step logic: first click shows validation errors
					setSimpleModeValidationFailed(true);
					setIsValidating(false);
					console.log('🔍 NodeDrawer.handleSave - Validation failed, staying in node');
					return;
				} else {
					setSimpleModeValidationFailed(false);
					console.log('🔍 NodeDrawer.handleSave - Validation passed');
				}
			} catch (error) {
				console.error('Validation error:', error);
				setSimpleModeValidationAttempted(true);
				setSimpleModeValidationFailed(true);
				setIsValidating(false);
				return;
			}
			setIsValidating(false);
		}

		// If validation was already attempted and failed, this is the second click
		// Allow the user to save despite validation failures
		if (simpleModeValidationAttempted && simpleModeValidationFailed) {
			// Second click: save despite validation failures
			console.log(
				'🔍 NodeDrawer.handleSave - Second click, saving despite validation failures'
			);
			onSave(localData);
			onClose();

			// Reset validation state for next time
			setSimpleModeValidationAttempted(false);
			setSimpleModeValidationFailed(false);
			return;
		}

		// First click with validation passed, or no validation function
		console.log('🔍 NodeDrawer.handleSave - First click or no validation, saving and closing');
		onSave(localData);
		onClose();

		// Reset validation state for next time
		setSimpleModeValidationAttempted(false);
		setSimpleModeValidationFailed(false);
	};

	const handleValidationRequest = (validate: () => Promise<boolean>) => {
		validationFunctionRef.current = validate;
	};

	const handleClose = async () => {
		// If there's a validation function, run it first to show any errors
		if (validationFunctionRef.current) {
			setIsValidating(true);
			setShowValidationErrors(true);
			try {
				const isValid = await validationFunctionRef.current();
				// Only close the drawer if validation passes
				if (isValid) {
					onClose();
				}
			} catch (error) {
				console.error('Validation error:', error);
				// Don't close on validation error
			}
			setIsValidating(false);
		} else {
			// No validation function, just close
			onClose();
		}
	};

	const handleLocalChange = (newData: CustomNodeData) => {
		setLocalData(newData);
	};

	const handleLabelChange = (newLabel: string) => {
		setLocalData(prev => {
			const updated = { ...prev, label: newLabel };

			// For start.form nodes, also update the config.name to keep them in sync
			if (prev.type === 'start.form' && prev.category === 'triggers') {
				const config = (prev as any).config || {};
				updated.config = { ...config, name: newLabel };
			}

			// For API nodes, we don't need to update config.name since they use the label directly
			// The label change should be sufficient for API nodes

			return updated;
		});

		// In wizard mode, we need to handle label changes differently
		// Instead of calling onSave (which triggers wizard completion), we'll use
		// the onChange callback if available, or just update local state
		if (wizardMode) {
			const updatedData = { ...localData, label: newLabel };

			// For start.form nodes, also update the config.name to keep them in sync
			if (localData.type === 'start.form' && localData.category === 'triggers') {
				const config = (localData as any).config || {};
				updatedData.config = { ...config, name: newLabel };
			}

			// If we have an onChange callback, use it to update the node data
			// This allows wizard nodes to handle label changes themselves
			if (onChange) {
				onChange(updatedData);
			}
			return;
		}

		// Debounce the save to prevent excessive auto-save triggers (for non-wizard nodes)
		if (labelSaveTimeoutRef.current) {
			clearTimeout(labelSaveTimeoutRef.current);
		}

		labelSaveTimeoutRef.current = setTimeout(() => {
			const updatedData = { ...localData, label: newLabel };

			// For start.form nodes, also update the config.name to keep them in sync
			if (localData.type === 'start.form' && localData.category === 'triggers') {
				const config = (localData as any).config || {};
				updatedData.config = { ...config, name: newLabel };
			}

			// Trigger save with debouncing for real-time updates
			onSave(updatedData);
		}, 500); // 500ms delay to prevent excessive saves
	};

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (labelSaveTimeoutRef.current) {
				clearTimeout(labelSaveTimeoutRef.current);
			}
		};
	}, []);

	const handleHelpClick = () => {
		setIsHelpMenuOpen(true);
		if (onHelpMenuOpen) {
			onHelpMenuOpen();
		}
	};

	const renderContent = () => {
		// If children are provided, render them instead of the default content
		if (children) {
			return children;
		}

		// Default content for other node types (fallback for when not using proper config component)
		return (
			<div className="p-6 space-y-4">
				{localData.category !== 'flow-control' && (
					<div>
						<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
							Configuration
						</label>
						<textarea
							value={JSON.stringify(
								(localData as ProcessingNodeData | IntegrationsNodeData).config ||
									{},
								null,
								2
							)}
							onChange={e => {
								try {
									const config = JSON.parse(e.target.value);
									handleLocalChange({
										...localData,
										config,
									} as CustomNodeData);
								} catch (error) {
									// Handle invalid JSON
								}
							}}
							className="px-3 py-2 w-full h-48 font-mono text-sm placeholder-gray-500 text-gray-900 bg-white rounded-md border border-gray-300 transition-colors dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none"
						/>
					</div>
				)}
			</div>
		);
	};

	return (
		<>
			<Dialog.Root
				open={isOpen}
				onOpenChange={open => {
					// Don't close the drawer if the help menu is open
					if (!open && isHelpMenuOpen) {
						return;
					}
					onClose();
				}}
			>
				<Dialog.Portal>
					<Dialog.Overlay className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
					<Dialog.Content className="fixed inset-0 z-50 bg-white dark:bg-gray-800 shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-right-1/2 data-[state=open]:slide-in-from-right-1/2 transition-colors">
						<div className="flex flex-col h-full">
							<div className="flex justify-between items-center px-6 py-4 border-b border-gray-200 dark:border-gray-700">
								<div className="flex-1">
									<Dialog.Title asChild>
										<div className="flex gap-2 items-center text-lg font-semibold text-gray-900 dark:text-white">
											<span>Edit Node:</span>
											<input
												type="text"
												value={localData.label}
												onChange={e => handleLabelChange(e.target.value)}
												className="flex-1 px-2 py-1 min-w-0 text-lg font-semibold text-gray-900 bg-transparent rounded-sm border-b-2 border-transparent transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none dark:text-white"
												placeholder="Enter node name..."
											/>
											<span className="text-sm text-gray-500 dark:text-gray-400">
												({localData.type})
											</span>
										</div>
									</Dialog.Title>
								</div>
								<div className="flex gap-2 items-center">
									<button
										onClick={handleHelpClick}
										className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
										title={`View help for ${localData.type} node`}
									>
										<HelpCircle size={16} />
										Help
									</button>
									<button
										onClick={handleClose}
										className="p-2 text-gray-600 rounded-full transition-colors hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400"
									>
										<X className="w-5 h-5" />
									</button>
								</div>
							</div>
							<div className="overflow-y-auto flex-1 bg-gray-50 dark:bg-gray-900">
								{/* Validation Results Display - Only show when no children are provided */}
								{showValidationErrors && !children && (
									<div className="px-6 py-4 bg-white border-b border-gray-200 dark:border-gray-700 dark:bg-gray-800">
										<ValidationResultDisplay
											errors={[]} // This will be populated by the config component
											isValidating={isValidating}
											onDismiss={() => setShowValidationErrors(false)}
										/>
									</div>
								)}

								{renderContent()}
							</div>

							{/* Dynamic Footer - Wizard Navigation or Save/Cancel */}
							{wizardMode ? (
								// Wizard Navigation Footer
								<div className="flex-shrink-0 px-6 py-4 bg-gray-50 border-t border-gray-200 shadow-lg dark:border-gray-700 dark:bg-gray-800">
									{/* Validation Status Message */}
									{validationFailed && currentStep === totalSteps && (
										<div className="mb-3 p-3 bg-orange-50 border border-orange-200 rounded-md dark:bg-orange-900/20 dark:border-orange-800">
											<div className="flex items-start gap-2">
												<AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0 dark:text-orange-400" />
												<div className="text-sm text-orange-800 dark:text-orange-200">
													<strong>Validation failed.</strong> You can
													review the errors above and fix them, or click
													"Complete Setup Anyway" to save and exit the
													node.
												</div>
											</div>
										</div>
									)}
									<div className="flex justify-between items-center">
										<button
											onClick={onPreviousStep}
											disabled={currentStep === 1}
											className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
										>
											← Previous
										</button>

										{/* Progress indicator in footer */}
										<div className="flex gap-2 items-center text-sm text-gray-600 dark:text-gray-400">
											<span>
												Step {currentStep} of {totalSteps}
											</span>
											<div className="w-20 h-2 bg-gray-200 rounded-full dark:bg-gray-700">
												<div
													className="h-2 bg-blue-600 rounded-full transition-all duration-300"
													style={{
														width: `${
															(currentStep / totalSteps) * 100
														}%`,
													}}
												/>
											</div>
										</div>

										<div className="flex gap-3">
											{currentStep < totalSteps && (
												<button
													onClick={onNextStep}
													disabled={!canProceedToNext}
													className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent transition-colors hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
												>
													Next →
												</button>
											)}

											{currentStep === totalSteps && (
												<button
													onClick={async () => {
														if (onCompleteWizard) {
															await onCompleteWizard();
															// The onCompleteWizard function will handle whether to close the drawer
															// based on validation results. Don't automatically close here.
														}
													}}
													disabled={isCompletingSetup}
													className={`px-4 py-2 text-sm font-medium text-white rounded-md border border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
														validationFailed
															? 'bg-orange-600 hover:bg-orange-700'
															: 'bg-green-600 hover:bg-green-700'
													}`}
												>
													{isCompletingSetup ? (
														<div className="flex items-center gap-2">
															<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
															Validating...
														</div>
													) : validationFailed ? (
														'⚠️ Complete Setup Anyway'
													) : (
														'✨ Complete Setup'
													)}
												</button>
											)}
										</div>
									</div>
								</div>
							) : (
								// Normal Save/Cancel Footer
								<div className="flex flex-col px-6 py-4 space-y-3 border-t border-gray-200 dark:border-gray-700">
									{/* Validation status message for simple mode */}
									{simpleModeValidationFailed && (
										<div className="flex items-start gap-2 p-3 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md">
											<div className="flex items-start gap-2">
												<AlertCircle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0 dark:text-orange-400" />
												<div className="text-sm text-orange-800 dark:text-orange-200">
													<strong>Validation failed.</strong> You can
													review the errors above and fix them, or click
													"Save Changes Anyway" to save and exit the node.
												</div>
											</div>
										</div>
									)}
									<div className="flex justify-end space-x-2">
										<button
											onClick={handleClose}
											className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm transition-colors dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
										>
											Cancel
										</button>
										<button
											onClick={handleSave}
											disabled={isValidating}
											className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
												simpleModeValidationFailed
													? 'bg-orange-600 hover:bg-orange-700'
													: 'bg-teal-500 hover:bg-teal-600 dark:bg-teal-500 dark:hover:bg-teal-600'
											}`}
										>
											{isValidating
												? 'Validating...'
												: simpleModeValidationFailed
												? '⚠️ Save Changes Anyway'
												: 'Save Changes'}
										</button>
									</div>
								</div>
							)}
						</div>
					</Dialog.Content>
				</Dialog.Portal>
			</Dialog.Root>

			{/* Help Menu */}
			<HelpMenu
				isOpen={isHelpMenuOpen}
				onClose={() => {
					setIsHelpMenuOpen(false);
					if (onHelpMenuOpen) {
						// Notify parent that help menu is closed
						// This is a bit of a hack, but we need to sync the state
						setTimeout(() => {
							if (onHelpMenuOpen) {
								onHelpMenuOpen();
							}
						}, 0);
					}
				}}
				initialNodeId={localData.type}
				embedded={true}
				popup={true}
			/>
		</>
	);
}
